import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Archive,
  CircleCheck,
  Edit3,
  Flag,
  Languages,
  Mail,
  MessageSquare,
  Monitor,
  MoreVertical,
  Palette,
  Plus,
  Settings,
  Share2,
  Sparkles,
  Store,
  Sun,
  Moon,
  Trash2,
  UserCircle,
  Users,
  Crown,
} from 'lucide-react'
import {
  accountSignOut,
  correctItemCategory,
  deleteCurrentAccount,
  loadLayout,
  sendFeedback,
  updateItem,
  updateListMetadata,
} from '../../data'
import type {
  CloudCategory,
  CloudLayout,
  CloudLayoutSection,
  CloudShoppingItem,
  CloudShoppingList,
  CloudStore,
  CloudUserSettings,
  Language,
  ListInvite,
  ThemeMode,
} from '../../models'
import { addManualItem, archiveList, clearCompletedItems } from '../parityData'
import {
  recordCommunityItemEvidence,
  respondToInviteWithRebind,
  searchCommunityItems,
  type AccessibleListBinding,
  type CommunitySuggestion,
  type PremiumEntitlementState,
  type ReportTarget,
} from '../services/parityBackend'
import ListAccessDialog, { ReportDialog } from '../components/ListAccessDialog'
import {
  BottomNav,
  Card,
  Chip,
  copy,
  errorMessage,
  Fab,
  Field,
  FilledButton,
  IconButton,
  Modal,
  Notice,
  OutlinedButton,
  Screen,
  TextButton,
  TopBar,
} from '../components/ParityUi'

export interface CoreCtx {
  user: User
  language: Language
  settings: CloudUserSettings
  lists: CloudShoppingList[]
  listsReady: boolean
  stores: CloudStore[]
  categories: CloudCategory[]
  invites: ListInvite[]
  premium: PremiumEntitlementState
  accessibleBinding: AccessibleListBinding | null
  setLanguage: (language: Language) => void
  setThemeMode: (mode: ThemeMode) => void
  setCheckedBehavior: (behavior: string) => void
}

const suffixOf = (item: CloudShoppingItem) => item.note?.trim() || item.quantityText?.trim() || ''
const byPosition = (a: CloudShoppingItem, b: CloudShoppingItem) => a.manualSortPosition - b.manualSortPosition || a.createdAt - b.createdAt
const categoryName = (section: CloudLayoutSection, language: Language) => section.categoryNames[language] || section.categoryNames.en || section.categoryNames.sv || section.categoryId

export function HomeScreen({ ctx }: { ctx: CoreCtx }) {
  const navigate = useNavigate()
  const [rename, setRename] = useState<CloudShoppingList | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [menuListId, setMenuListId] = useState<string | null>(null)
  const activeLists = ctx.lists.filter((list) => list.status === 'active')
  const storeMap = useMemo(() => new Map(ctx.stores.map((store) => [store.id, store.name])), [ctx.stores])

  return <Screen className="with-bottom-nav">
    <TopBar title="SHELFWAY" actions={ctx.invites.length > 0 ? <TextButton onClick={() => navigate('/pwa/invites')}><Mail /> {ctx.invites.length}</TextButton> : undefined} />
    <div className="screen-scroll card-stack">
      {ctx.invites.length > 0 && <Card className="invite-banner" onClick={() => navigate('/pwa/invites')}><Mail /><div><strong>{copy(ctx.language, 'Shared list invitation', 'Inbjudan till delad lista')}</strong><small>{copy(ctx.language, `${ctx.invites.length} waiting`, `${ctx.invites.length} väntar`)}</small></div></Card>}
      {ctx.listsReady && activeLists.length === 0 && <Card><strong>{copy(ctx.language, 'No lists yet', 'Inga listor ännu')}</strong><p className="muted">{copy(ctx.language, "Create a list when you're ready to shop.", 'Skapa en lista när du är redo att handla.')}</p></Card>}
      {!ctx.listsReady && <div className="center-empty"><div className="parity-spinner" /><span className="muted">{copy(ctx.language, 'Loading lists…', 'Laddar listor…')}</span></div>}
      {activeLists.map((list) => {
        const owner = list.ownerUserId === ctx.user.uid
        const checked = list.items.filter((item) => item.isChecked).length
        return <Card key={list.id} className="list-card">
          <button type="button" className="card-main" onClick={() => navigate(`/pwa/list/${list.id}`)}>
            <strong>{list.name}</strong>
            {list.storeId && storeMap.get(list.storeId) && <small>{storeMap.get(list.storeId)}</small>}
            {!owner && <small className="shared-list-label"><Share2 />{copy(ctx.language, 'Shared list', 'Delad lista')}</small>}
            <span className="primary-label">{checked}/{list.items.length}</span>
          </button>
          {owner && <div className="overflow-wrap">
            <IconButton label={copy(ctx.language, 'List actions', 'Liståtgärder')} onClick={() => setMenuListId(menuListId === list.id ? null : list.id)}><MoreVertical /></IconButton>
            {menuListId === list.id && <div className="overflow-popover">
              <button type="button" onClick={() => { setMenuListId(null); setRename(list); setRenameValue(list.name) }}><Edit3 />{copy(ctx.language, 'Rename', 'Byt namn')}</button>
              <button type="button" onClick={() => { setMenuListId(null); navigate(`/pwa/list/${list.id}/retarget`) }}><Store />{copy(ctx.language, 'Store & shopping order', 'Butik & butiksordning')}</button>
              <button type="button" onClick={() => { setMenuListId(null); void archiveList(list) }}><Archive />{copy(ctx.language, 'Archive', 'Arkivera')}</button>
            </div>}
          </div>}
        </Card>
      })}
    </div>
    <Fab label={copy(ctx.language, 'New list', 'Ny lista')} onClick={() => navigate('/pwa/create')} />
    <BottomNav active="home" navigate={navigate} language={ctx.language} />
    {rename && <Modal title={copy(ctx.language, 'Rename list', 'Byt namn på lista')} onClose={() => setRename(null)}>
      <Field label={copy(ctx.language, 'List name', 'Listnamn')}><input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} /></Field>
      <div className="dialog-actions"><TextButton onClick={() => setRename(null)}>{copy(ctx.language, 'Cancel', 'Avbryt')}</TextButton><TextButton disabled={!renameValue.trim()} onClick={() => { void updateListMetadata({ ...rename, name: renameValue.trim() }); setRename(null) }}>{copy(ctx.language, 'Save', 'Spara')}</TextButton></div>
    </Modal>}
  </Screen>
}

export function InvitationsScreen({ ctx }: { ctx: CoreCtx }) {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const answer = async (invite: ListInvite, accept: boolean) => {
    setBusyId(invite.id)
    setError('')
    try {
      await respondToInviteWithRebind(invite, accept, ctx.accessibleBinding)
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusyId(null)
    }
  }
  return <Screen>
    <TopBar title={copy(ctx.language, 'Shared lists', 'Delade listor')} back={() => navigate('/pwa/')} />
    <div className="screen-scroll card-stack">
      {error && <Notice error>{error}</Notice>}
      {ctx.invites.length === 0 ? <div className="center-empty"><Users /><strong>{copy(ctx.language, 'No invitations', 'Inga inbjudningar')}</strong></div> : ctx.invites.map((invite) => <Card key={invite.id}>
        <strong>{invite.listName}</strong>
        {invite.ownerEmail && <p className="muted">{copy(ctx.language, `Invited by ${invite.ownerEmail}`, `Inbjuden av ${invite.ownerEmail}`)}</p>}
        <div className="two-buttons invite-actions"><FilledButton disabled={busyId === invite.id} onClick={() => void answer(invite, true)}>{copy(ctx.language, 'Accept', 'Acceptera')}</FilledButton><OutlinedButton disabled={busyId === invite.id} onClick={() => void answer(invite, false)}>{copy(ctx.language, 'Decline', 'Avböj')}</OutlinedButton></div>
      </Card>)}
    </div>
  </Screen>
}

function categoryOptions(ctx: CoreCtx, layout: CloudLayout | null) {
  if (layout) return layout.sections.slice().sort((a, b) => a.displayOrder - b.displayOrder).map((section) => ({ id: section.categoryId, name: categoryName(section, ctx.language) }))
  return ctx.categories.map((category) => ({
    id: category.id,
    name: category.translations.find((translation) => translation.language === ctx.language)?.name
      || category.translations.find((translation) => translation.language === 'en')?.name
      || category.id,
  })).sort((a, b) => a.name.localeCompare(b.name))
}

export function ShoppingScreen({ ctx }: { ctx: CoreCtx }) {
  const { listId } = useParams()
  const navigate = useNavigate()
  const list = ctx.lists.find((row) => row.id === listId)
  const [layout, setLayout] = useState<CloudLayout | null>(null)
  const [accessOpen, setAccessOpen] = useState(false)
  const [clearConfirm, setClearConfirm] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (list?.layoutId) void loadLayout(list.layoutId).then(setLayout).catch((cause) => setMessage(errorMessage(cause)))
    else setLayout(null)
  }, [list?.layoutId])

  useEffect(() => {
    if (ctx.listsReady && listId && !list) navigate('/pwa/', { replace: true })
  }, [ctx.listsReady, list, listId, navigate])

  if (!list) return <Screen><TopBar title={copy(ctx.language, 'List', 'Lista')} back={() => navigate('/pwa/')} /><div className="screen-scroll"><Notice>{copy(ctx.language, 'List unavailable. It may still be syncing or your access was removed.', 'Listan är inte tillgänglig. Den kan fortfarande synkas eller så har din åtkomst tagits bort.')}</Notice></div></Screen>

  const checked = list.items.filter((item) => item.isChecked).length
  const total = list.items.length
  const allDone = total > 0 && checked === total
  const owner = list.ownerUserId === ctx.user.uid
  const canEdit = owner || list.memberRole !== 'viewer'
  const storeName = list.storeId ? ctx.stores.find((store) => store.id === list.storeId)?.name : undefined
  const sections = layout?.sections.slice().sort((a, b) => a.displayOrder - b.displayOrder) ?? []
  const sectionOrder = new Map(sections.map((section, index) => [section.categoryId, index]))
  const doneSection = ctx.settings.checkedItemBehavior === 'done_section'
  const normalItems = doneSection ? list.items.filter((item) => !item.isChecked) : list.items
  const groups = new Map<string, CloudShoppingItem[]>()
  normalItems.slice().sort((a, b) => (sectionOrder.get(a.categoryId ?? '') ?? 9999) - (sectionOrder.get(b.categoryId ?? '') ?? 9999) || byPosition(a, b)).forEach((item) => {
    const key = item.categoryId || '__other'
    groups.set(key, [...(groups.get(key) ?? []), item])
  })
  const titleFor = (key: string) => {
    const section = sections.find((candidate) => candidate.categoryId === key)
    return section ? categoryName(section, ctx.language) : key === '__other' ? copy(ctx.language, 'Other', 'Övrigt') : key
  }

  return <Screen className="shopping-page">
    <TopBar
      title={list.name}
      subtitle={storeName}
      back={() => navigate('/pwa/')}
      actions={<>
        <IconButton label={copy(ctx.language, 'Manage access', 'Hantera åtkomst')} onClick={() => setAccessOpen(true)}><Users /></IconButton>
        {canEdit && <IconButton label={copy(ctx.language, 'Add item', 'Lägg till vara')} onClick={() => navigate(`/pwa/list/${list.id}/add`)}><Plus /></IconButton>}
      </>}
    />
    <div className="progress-block"><strong>{checked} / {total}</strong><div className="linear-progress"><span style={{ width: `${total ? checked / total * 100 : 0}%` }} /></div></div>
    <div className="shopping-scroll">
      {message && <Notice error>{message}</Notice>}
      {total === 0 ? <div className="empty-shopping"><h2>{copy(ctx.language, 'Your list is empty', 'Din lista är tom')}</h2>{canEdit && <FilledButton onClick={() => navigate(`/pwa/list/${list.id}/add`)}>{copy(ctx.language, 'Add item', 'Lägg till vara')}</FilledButton>}</div> : <>
        {[...groups.entries()].map(([key, items]) => <ShoppingSection key={key} title={titleFor(key)} items={items} list={list} layout={layout} ctx={ctx} canEdit={canEdit} />)}
        {doneSection && checked > 0 && <ShoppingSection title={copy(ctx.language, 'Done', 'Klart')} done items={list.items.filter((item) => item.isChecked).sort(byPosition)} list={list} layout={layout} ctx={ctx} canEdit={canEdit} />}
      </>}
    </div>
    <div className="shopping-bottom">{allDone && canEdit ? <FilledButton className="full" onClick={() => setClearConfirm(true)}>{copy(ctx.language, 'Clear completed list', 'Rensa färdig lista')}</FilledButton> : <strong>{copy(ctx.language, `${total - checked} left`, `${total - checked} kvar`)}</strong>}</div>
    {accessOpen && <ListAccessDialog language={ctx.language} list={list} currentUid={ctx.user.uid} onClose={() => setAccessOpen(false)} onLeft={() => navigate('/pwa/', { replace: true })} onRevoked={() => navigate('/pwa/', { replace: true })} />}
    {clearConfirm && <Modal title={copy(ctx.language, 'Clear list?', 'Rensa listan?')} onClose={() => setClearConfirm(false)}><p>{copy(ctx.language, 'All completed items will be removed.', 'Alla färdigmarkerade varor tas bort.')}</p><div className="dialog-actions"><TextButton onClick={() => setClearConfirm(false)}>{copy(ctx.language, 'Cancel', 'Avbryt')}</TextButton><FilledButton onClick={() => { setClearConfirm(false); void clearCompletedItems(list) }}>{copy(ctx.language, 'Clear', 'Rensa')}</FilledButton></div></Modal>}
  </Screen>
}

function ShoppingSection({ title, done = false, items, list, layout, ctx, canEdit }: { title: string; done?: boolean; items: CloudShoppingItem[]; list: CloudShoppingList; layout: CloudLayout | null; ctx: CoreCtx; canEdit: boolean }) {
  return <section className="shopping-section"><div className={`section-header ${done ? 'done' : ''}`}>{title.toUpperCase()}</div>{items.map((item) => <SwipeItemRow key={item.id} item={item} list={list} layout={layout} ctx={ctx} canEdit={canEdit} />)}</section>
}

function SwipeItemRow({ item, list, layout, ctx, canEdit }: { item: CloudShoppingItem; list: CloudShoppingList; layout: CloudLayout | null; ctx: CoreCtx; canEdit: boolean }) {
  const ACTION_WIDTH = 168
  const THRESHOLD = ACTION_WIDTH * 0.35
  const [offset, setOffset] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [reporting, setReporting] = useState(false)
  const start = useRef<{ x: number; base: number } | null>(null)
  const moved = useRef(false)

  const pointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canEdit) return
    start.current = { x: event.clientX, base: offset }
    moved.current = false
    setDragging(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }
  const pointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!start.current || !canEdit) return
    const delta = event.clientX - start.current.x
    if (Math.abs(delta) > 7) moved.current = true
    setOffset(Math.max(-ACTION_WIDTH, Math.min(0, start.current.base + delta)))
  }
  const finishDrag = () => {
    if (!start.current) return
    const base = start.current.base
    setDragging(false)
    start.current = null
    setOffset((current) => {
      if (base === 0) return current <= -THRESHOLD ? -ACTION_WIDTH : 0
      return current >= -ACTION_WIDTH + THRESHOLD ? 0 : -ACTION_WIDTH
    })
  }
  const toggle = () => {
    if (!canEdit) return
    void updateItem(list.id, item, { isChecked: !item.isChecked, checkedAt: !item.isChecked ? Date.now() : null })
  }
  const rowClick = () => {
    if (moved.current) { moved.current = false; return }
    if (offset < 0) setOffset(0)
    else toggle()
  }

  return <>
    <div className="swipe-shell">
      <div className="swipe-actions">
        <button type="button" className="edit" aria-label={copy(ctx.language, `Edit ${item.displayTitle}`, `Redigera ${item.displayTitle}`)} onClick={() => { setOffset(0); setEditing(true) }}><Edit3 /><span>{copy(ctx.language, 'Edit', 'Redigera')}</span></button>
        <button type="button" className="delete" aria-label={copy(ctx.language, `Delete ${item.displayTitle}`, `Ta bort ${item.displayTitle}`)} onClick={() => { setOffset(0); setDeleting(true) }}><Trash2 /><span>{copy(ctx.language, 'Delete', 'Ta bort')}</span></button>
      </div>
      <div
        className={`shopping-item ${item.isChecked ? 'checked' : ''} ${dragging ? 'dragging' : ''}`}
        style={{ transform: `translateX(${offset}px)` }}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onClick={rowClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); rowClick() } }}
      >
        <button type="button" className={`item-check ${item.isChecked ? 'checked' : ''}`} disabled={!canEdit} aria-label={item.isChecked ? copy(ctx.language, 'Mark not done', 'Markera som ej klar') : copy(ctx.language, 'Mark done', 'Markera klar')} onClick={(event) => { event.stopPropagation(); toggle() }}>{item.isChecked && <CircleCheck />}</button>
        <div className="item-line"><strong>{item.displayTitle}</strong>{suffixOf(item) && <span> · {suffixOf(item)}</span>}</div>
      </div>
    </div>
    {editing && <EditItemDialog ctx={ctx} list={list} item={item} layout={layout} onClose={() => setEditing(false)} onReport={() => { setEditing(false); setReporting(true) }} />}
    {deleting && <Modal title={copy(ctx.language, `Delete ${item.displayTitle}?`, `Ta bort ${item.displayTitle}?`)} onClose={() => setDeleting(false)}><p>{copy(ctx.language, 'This item will be removed from the shopping list.', 'Varan tas bort från inköpslistan.')}</p><div className="dialog-actions"><TextButton onClick={() => setDeleting(false)}>{copy(ctx.language, 'Cancel', 'Avbryt')}</TextButton><TextButton className="danger-text" onClick={() => { setDeleting(false); void updateItem(list.id, item, { deletedAt: Date.now() }) }}>{copy(ctx.language, 'Delete', 'Ta bort')}</TextButton></div></Modal>}
    {reporting && <ReportDialog language={ctx.language} target={{ targetType: 'shared_list_item', targetId: item.id, source: 'shared_list_item', listId: list.id, label: item.displayTitle }} onClose={() => setReporting(false)} />}
  </>
}

function EditItemDialog({ ctx, list, item, layout, onClose, onReport }: { ctx: CoreCtx; list: CloudShoppingList; item: CloudShoppingItem; layout: CloudLayout | null; onClose: () => void; onReport: () => void }) {
  const [title, setTitle] = useState(item.displayTitle)
  const [suffix, setSuffix] = useState(suffixOf(item))
  const [category, setCategory] = useState(item.categoryId || '')
  const options = categoryOptions(ctx, layout)
  const save = async () => {
    if (!title.trim() || !category) return
    await updateItem(list.id, item, { displayTitle: title.trim(), originalInput: title.trim(), note: suffix.trim() || null, quantityText: null })
    if (category !== item.categoryId) {
      await correctItemCategory(list, item, category)
      const label = options.find((option) => option.id === category)?.name
      if (label) void recordCommunityItemEvidence(ctx.language, title.trim(), label).catch(() => undefined)
    }
    onClose()
  }
  return <Modal title={copy(ctx.language, 'Edit item', 'Redigera vara')} onClose={onClose}>
    <Field label={copy(ctx.language, 'Item name', 'Varunamn')}><input value={title} onChange={(event) => setTitle(event.target.value)} /></Field>
    <Field label={copy(ctx.language, 'Notes', 'Anteckning')}><input value={suffix} onChange={(event) => setSuffix(event.target.value)} /></Field>
    {options.length > 0 && <><h3>{copy(ctx.language, 'Category', 'Kategori')}</h3><div className="chip-scroll wrap">{options.map((option) => <Chip key={option.id} selected={category === option.id} onClick={() => setCategory(option.id)}>{option.name}</Chip>)}</div></>}
    <div className="dialog-actions edit-dialog-actions"><TextButton onClick={onReport}><Flag />{copy(ctx.language, 'Report item', 'Rapportera vara')}</TextButton><TextButton onClick={onClose}>{copy(ctx.language, 'Cancel', 'Avbryt')}</TextButton><FilledButton disabled={!title.trim() || !category} onClick={() => void save()}>{copy(ctx.language, 'Save', 'Spara')}</FilledButton></div>
  </Modal>
}

export function AddItemScreen({ ctx }: { ctx: CoreCtx }) {
  const { listId } = useParams()
  const navigate = useNavigate()
  const list = ctx.lists.find((row) => row.id === listId)
  const [layout, setLayout] = useState<CloudLayout | null>(null)
  const [title, setTitle] = useState('')
  const [suffix, setSuffix] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<CommunitySuggestion[]>([])
  const [suggestionsBusy, setSuggestionsBusy] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (list?.layoutId) void loadLayout(list.layoutId).then(setLayout).catch(() => setLayout(null))
    else setLayout(null)
  }, [list?.layoutId])

  const options = list ? categoryOptions(ctx, layout) : []
  useEffect(() => {
    if (!ctx.premium.isPremium || title.trim().length < 2 || options.length === 0) {
      setSuggestions([])
      return
    }
    let active = true
    const timer = window.setTimeout(() => {
      setSuggestionsBusy(true)
      void searchCommunityItems(ctx.language, title, options).then((rows) => {
        if (active) setSuggestions(rows)
      }).catch(() => {
        if (active) setSuggestions([])
      }).finally(() => {
        if (active) setSuggestionsBusy(false)
      })
    }, 180)
    return () => { active = false; window.clearTimeout(timer) }
  }, [ctx.language, ctx.premium.isPremium, title, options.map((option) => `${option.id}:${option.name}`).join('|')])

  if (!list) return <Screen><TopBar title={copy(ctx.language, 'Add item', 'Lägg till vara')} back={() => navigate('/pwa/')} /><div className="screen-scroll"><Notice>{copy(ctx.language, 'List unavailable.', 'Listan är inte tillgänglig.')}</Notice></div></Screen>

  const add = () => {
    if (!title.trim() || !category) return
    const label = options.find((option) => option.id === category)?.name
    addManualItem(list, title, suffix, category)
    if (label) void recordCommunityItemEvidence(ctx.language, title, label).catch(() => undefined)
    setTitle('')
    setSuffix('')
    setCategory(null)
    setSuggestions([])
    setMessage(copy(ctx.language, 'Item added', 'Vara tillagd'))
  }

  return <Screen>
    <TopBar title={copy(ctx.language, 'Add item', 'Lägg till vara')} back={() => navigate(`/pwa/list/${list.id}`)} />
    <div className="screen-scroll form-stack">
      {message && <Notice>{message}</Notice>}
      <Field label={copy(ctx.language, 'Item name', 'Varunamn')}><input autoFocus value={title} placeholder={copy(ctx.language, 'Milk', 'Mjölk')} onChange={(event) => setTitle(event.target.value)} /></Field>
      {ctx.premium.isPremium && (suggestionsBusy || suggestions.length > 0) && <div className="community-suggestions">
        <div className="community-suggestion-heading"><Sparkles /><strong>{copy(ctx.language, 'Community suggestions', 'Community-förslag')}</strong>{suggestionsBusy && <span className="mini-spinner" />}</div>
        {suggestions.map((suggestion) => <button key={suggestion.communityItemId} type="button" onClick={() => { setTitle(suggestion.title); setCategory(suggestion.categoryId); setSuggestions([]) }}><span><strong>{suggestion.title}</strong><small>{suggestion.categoryName}</small></span><small>{suggestion.votes}×</small></button>)}
      </div>}
      {!ctx.premium.isPremium && <p className="muted premium-inline-hint"><Crown />{copy(ctx.language, 'Premium adds community autocomplete and smart category defaults.', 'Premium lägger till community-autocomplete och smarta standardkategorier.')}</p>}
      <Field label={copy(ctx.language, 'Notes', 'Anteckning')}><input value={suffix} placeholder={copy(ctx.language, '2 kg, 2 pcs, large…', '2 kg, 2 st, stor…')} onChange={(event) => setSuffix(event.target.value)} /></Field>
      <h3>{copy(ctx.language, 'Category · required', 'Kategori · obligatorisk')}</h3>
      {options.length === 0 ? <p className="error-text">{copy(ctx.language, 'No categories are available for this list.', 'Det finns inga kategorier för den här listan.')}</p> : <div className="chip-scroll wrap">{options.map((option) => <Chip key={option.id} selected={category === option.id} onClick={() => setCategory(option.id)}>{option.name}</Chip>)}</div>}
      <FilledButton className="full large" disabled={!title.trim() || !category} onClick={add}>{copy(ctx.language, 'Add item', 'Lägg till vara')}</FilledButton>
      <OutlinedButton className="full" onClick={() => navigate(`/pwa/list/${list.id}/add-legacy`)}>{copy(ctx.language, 'Add many items with AI', 'Lägg till många varor med AI')}</OutlinedButton>
    </div>
  </Screen>
}

function SettingsCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return <Card><div className="settings-card-title">{icon}<strong>{title}</strong></div><div className="settings-card-content">{children}</div></Card>
}

function FeedbackDialog({ ctx, onClose, onSent }: { ctx: CoreCtx; onClose: () => void; onSent: () => void }) {
  const [category, setCategory] = useState('idea')
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      await sendFeedback(category, title, message, ctx.language)
      onSent()
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }
  return <Modal title={copy(ctx.language, 'Send feedback', 'Skicka feedback')} onClose={onClose}>
    {error && <Notice error>{error}</Notice>}
    <div className="chip-scroll wrap">{[
      ['idea', copy(ctx.language, 'Idea', 'Idé')], ['bug', 'Bug'], ['store_data', copy(ctx.language, 'Store data', 'Butiksdata')], ['other', copy(ctx.language, 'Other', 'Övrigt')],
    ].map(([id, label]) => <Chip key={id} selected={category === id} onClick={() => setCategory(id)}>{label}</Chip>)}</div>
    <Field label={copy(ctx.language, 'Title', 'Rubrik')}><input value={title} onChange={(event) => setTitle(event.target.value)} /></Field>
    <Field label={copy(ctx.language, 'Message', 'Meddelande')}><textarea rows={7} value={message} onChange={(event) => setMessage(event.target.value)} /></Field>
    <div className="dialog-actions"><TextButton onClick={onClose}>{copy(ctx.language, 'Cancel', 'Avbryt')}</TextButton><FilledButton disabled={busy || title.trim().length < 3 || message.trim().length < 10} onClick={() => void submit()}>{copy(ctx.language, 'Send', 'Skicka')}</FilledButton></div>
  </Modal>
}

export function SettingsScreen({ ctx }: { ctx: CoreCtx }) {
  const navigate = useNavigate()
  const [feedback, setFeedback] = useState(false)
  const [feedbackStatus, setFeedbackStatus] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const playStore = 'https://play.google.com/store/apps/details?id=se.hampusandersson.aisle'
  const manageSubscription = 'https://play.google.com/store/account/subscriptions?sku=shelfway_premium&package=se.hampusandersson.aisle'

  const deleteAccount = async () => {
    setDeleteConfirm(false)
    try { await deleteCurrentAccount() } catch (cause) { window.alert(errorMessage(cause)) }
  }

  return <Screen className="with-bottom-nav">
    <TopBar title={copy(ctx.language, 'Settings', 'Inställningar')} />
    <div className="screen-scroll settings-stack">
      <SettingsCard icon={<Palette />} title={copy(ctx.language, 'Appearance', 'Utseende')}><div className="chip-scroll"><Chip selected={ctx.settings.themeMode === 'system'} onClick={() => ctx.setThemeMode('system')}><Monitor />{copy(ctx.language, 'Auto', 'Auto')}</Chip><Chip selected={ctx.settings.themeMode === 'light'} onClick={() => ctx.setThemeMode('light')}><Sun />{copy(ctx.language, 'Light', 'Ljust')}</Chip><Chip selected={ctx.settings.themeMode === 'dark'} onClick={() => ctx.setThemeMode('dark')}><Moon />{copy(ctx.language, 'Dark', 'Mörkt')}</Chip></div></SettingsCard>
      <SettingsCard icon={<Languages />} title={copy(ctx.language, 'Language', 'Språk')}><div className="chip-scroll"><Chip selected={ctx.language === 'en'} onClick={() => ctx.setLanguage('en')}>English</Chip><Chip selected={ctx.language === 'sv'} onClick={() => ctx.setLanguage('sv')}>Svenska</Chip></div></SettingsCard>
      <SettingsCard icon={<CircleCheck />} title={copy(ctx.language, 'Checked items', 'Klara varor')}><p className="muted">{copy(ctx.language, 'This setting only changes how checked items look on this device.', 'Den här inställningen ändrar bara hur klara varor visas på den här enheten.')}</p><div className="chip-scroll"><Chip selected={ctx.settings.checkedItemBehavior !== 'done_section'} onClick={() => ctx.setCheckedBehavior('keep_in_category')}>{copy(ctx.language, 'Keep in category', 'Behåll i kategori')}</Chip><Chip selected={ctx.settings.checkedItemBehavior === 'done_section'} onClick={() => ctx.setCheckedBehavior('done_section')}>{copy(ctx.language, 'Move to Done', 'Flytta till Klart')}</Chip></div></SettingsCard>
      <SettingsCard icon={<Crown />} title="SHELFWAY Premium">
        <strong>{ctx.premium.checking ? copy(ctx.language, 'Checking Premium status…', 'Kontrollerar Premium-status…') : ctx.premium.isPremium ? copy(ctx.language, 'Premium is active on this account.', 'Premium är aktivt på det här kontot.') : copy(ctx.language, 'Make adding repeat groceries faster while keeping normal shopping lists free.', 'Gör det snabbare att lägga till återkommande varor samtidigt som vanliga inköpslistor förblir gratis.')}</strong>
        <p className="muted premium-benefits">{copy(ctx.language, '• Community item autocomplete and smart category defaults\n• Learns from anonymous manual categorization across SHELFWAY\n• Ad-free shopping\n• More Premium automation can be added later', '• Community-autocomplete och smarta standardkategorier\n• Lär sig av anonym manuell kategorisering i SHELFWAY\n• Reklamfri shopping\n• Fler Premium-automationer kan läggas till senare')}</p>
        {ctx.premium.error && <Notice error>{copy(ctx.language, 'Premium status could not be verified. Premium features stay locked until the entitlement check succeeds.', 'Premium-status kunde inte verifieras. Premium-funktioner förblir låsta tills kontrollen lyckas.')}</Notice>}
        {ctx.premium.isPremium ? <OutlinedButton className="full" onClick={() => window.open(manageSubscription, '_blank', 'noopener,noreferrer')}>{copy(ctx.language, 'Manage subscription', 'Hantera prenumeration')}</OutlinedButton> : <>
          <p className="muted pwa-billing-note">{copy(ctx.language, 'Google Play Billing is an Android API and cannot run inside an iPhone/web PWA. Premium unlocks here automatically after the same Shelfway account has a valid server-verified entitlement.', 'Google Play Billing är ett Android-API och kan inte köras i en iPhone/webb-PWA. Premium låses upp här automatiskt när samma Shelfway-konto har en giltig serververifierad entitlement.')}</p>
          <OutlinedButton className="full" onClick={() => window.open(playStore, '_blank', 'noopener,noreferrer')}>{copy(ctx.language, 'Open Shelfway on Google Play', 'Öppna Shelfway på Google Play')}</OutlinedButton>
        </>}
      </SettingsCard>
      <SettingsCard icon={<MessageSquare />} title="Feedback"><p className="muted">{copy(ctx.language, 'Send an idea, bug report or incorrect store data directly from the app.', 'Skicka en idé, buggrapport eller felaktig butiksdata direkt från appen.')}</p>{feedbackStatus && <small className="primary-label">{feedbackStatus}</small>}<OutlinedButton className="full" onClick={() => setFeedback(true)}><MessageSquare />{copy(ctx.language, 'Send feedback', 'Skicka feedback')}</OutlinedButton></SettingsCard>
      <TextButton className="archive-link" onClick={() => navigate('/pwa/archive')}><Archive />{copy(ctx.language, 'Archive', 'Arkiv')}</TextButton>
      <SettingsCard icon={<UserCircle />} title={copy(ctx.language, 'Account', 'Konto')}>{ctx.user.displayName && <strong>{ctx.user.displayName}</strong>}<p className="muted">{ctx.user.email}</p><small className="muted">{copy(ctx.language, 'Lists, stores and shopping orders are synced with this account.', 'Listor, butiker och butiksordningar synkas med det här kontot.')}</small><OutlinedButton className="full" onClick={() => void accountSignOut()}>{copy(ctx.language, 'Sign out', 'Logga ut')}</OutlinedButton><TextButton className="full danger-text" onClick={() => setDeleteConfirm(true)}>{copy(ctx.language, 'Delete account', 'Radera konto')}</TextButton></SettingsCard>
    </div>
    <BottomNav active="settings" navigate={navigate} language={ctx.language} />
    {feedback && <FeedbackDialog ctx={ctx} onClose={() => setFeedback(false)} onSent={() => { setFeedback(false); setFeedbackStatus(copy(ctx.language, 'Feedback sent. Thank you!', 'Feedback skickad. Tack!')) }} />}
    {deleteConfirm && <Modal title={copy(ctx.language, 'Delete account?', 'Radera kontot?')} onClose={() => setDeleteConfirm(false)}><p>{copy(ctx.language, 'This permanently deletes your SHELFWAY account and its associated cloud data, including lists, stores and shopping orders. This cannot be undone.', 'Det här raderar permanent ditt SHELFWAY-konto och tillhörande molndata, inklusive listor, butiker och butiksordningar. Det går inte att ångra.')}</p><div className="dialog-actions"><TextButton onClick={() => setDeleteConfirm(false)}>{copy(ctx.language, 'Cancel', 'Avbryt')}</TextButton><TextButton className="danger-text" onClick={() => void deleteAccount()}>{copy(ctx.language, 'Delete account', 'Radera konto')}</TextButton></div></Modal>}
  </Screen>
}
